import { LocationOn as LocationOnIcon, LocationOnOutlined, MyLocation as MyLocationIcon, Search as SearchIcon } from "@mui/icons-material";
import { Avatar, Box, Button, Grid, Paper, Snackbar, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  nextViewConnectBusinessState,
  previousViewConnectBusinessState,
  updateConnectBusinessState,
} from "../redux/features/connectBusinessStateSlice";
import { MapPredictiveAlgorithm } from "./mapPredictiveAlgorithm";
import { SignUpNextButton } from "./signupNextButton";
import { connectBusinessFlowSx } from "./connectBusinessFlowStyles";
import { colors } from "./ui/colors";

export const ConnectBusinessAddress = ({ updateComponentState, componentState }) => {
  const currPage = "/signupEmployee";
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const connectBusinessState = useSelector((state) => state.connectBusinessState);
  const { currentView } = connectBusinessState;
  const [isFormValid, setIsFormValid] = useState(false);

  const [openLocationError, setOpenLocationError] = useState(false);
  const [locationErrorMessage, setLocationErrorMessage] = useState("");

  const handleLocationSelect = (city, state, zipCode, country, address) => {
    dispatch(updateConnectBusinessState({ address, state, city, zipCode, country }));
  };

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, 200);
    return () => clearTimeout(timeout);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateConnectBusinessState({ [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setOpen(true);
    const formData = new FormData(event.currentTarget);

    const userData = {
      phone: formData.get("phone"),
      age: formData.get("age"),
    };
    dispatch(updateConnectBusinessState(userData));
    dispatch(nextViewConnectBusinessState(userData));
    setIsLoading(false);
    setOpen(false);
    setIsFormValid(true);
  };

  const handleLocationError = (message) => {
    setLocationErrorMessage(message);
    setOpenLocationError(true);
  };

  const handleCloseErrorMessage = () => {
    setLocationErrorMessage("");
    setOpenLocationError(false);
  };

  const useMyLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        if (position.coords.longitude && position.coords.latitude) {
          dispatch(
            updateComponentState({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            }),
          );
          dispatch(nextViewConnectBusinessState());
        } else {
          handleLocationError("We could not obtain your location.");
        }
      });
    } else {
      handleLocationError("Geolocation not available.");
    }
  };

  return (
    <Box
      sx={{
        ...connectBusinessFlowSx.shell,
        bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
      }}
    >
      <Grid container sx={{ maxWidth: 600, mx: "auto" }}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              ...connectBusinessFlowSx.panel,
              borderRadius: 5,
              border: "none",
            }}
          >
            {/* Header Section */}
            <Box sx={{ mb: 4, textAlign: "center" }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  ...connectBusinessFlowSx.iconWrap,
                }}
              >
                <LocationOnOutlined sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography variant="h5" sx={connectBusinessFlowSx.title} gutterBottom>
                Business Location
              </Typography>
              <Typography variant="body1" sx={connectBusinessFlowSx.copy}>
                Enter your address to connect with nearby cleaning experts
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              {/* Search Section */}
              <Box
                sx={{
                  ...connectBusinessFlowSx.contentBand,
                  borderRadius: 5,
                  border: "none",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <SearchIcon sx={{ fontSize: 20, mr: 1, color: "text.secondary" }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Search Address
                  </Typography>
                </Box>

                <MapPredictiveAlgorithm
                  onLocationSelect={handleLocationSelect}
                  updateComponentState={updateComponentState}
                  componentState={componentState}
                  isMapPickerHidden={false}
                />
              </Box>

              {/* Quick Location Button */}
              <Box
                sx={{
                  ...connectBusinessFlowSx.contentBand,
                  mb: 3,
                  textAlign: "center",
                  borderRadius: 5,
                  border: "none",
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} sx={connectBusinessFlowSx.title} gutterBottom>
                  Quick Location Access
                </Typography>
                <Typography variant="body2" sx={{ ...connectBusinessFlowSx.copy, mb: 2 }}>
                  Use your current location for faster setup
                </Typography>
                <Button
                  onClick={useMyLocation}
                  variant="contained"
                  startIcon={<MyLocationIcon sx={{ fontSize: 24 }} />}
                  sx={{
                    ...connectBusinessFlowSx.primaryButton,
                    background: colors.orange.base,
                    color: "white",
                    fontWeight: 700,
                    textTransform: "none",
                    py: 1.2,
                    px: 4,
                    borderRadius: 2,
                    boxShadow: "none",
                    "&:hover": { bgcolor: colors.orange[600], boxShadow: "none" },
                    borderColor: colors.orange.base,
                    fontWeight: 500,
                    height: 35,
                  }}
                >
                  Use My Location
                </Button>
              </Box>

              {/* Navigation Buttons */}
              <Box sx={{ mt: 3 }}>
                <SignUpNextButton nextViewEntityState={nextViewConnectBusinessState} previousViewEntityState={previousViewConnectBusinessState} />
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={openLocationError}
        autoHideDuration={3000}
        onClose={handleCloseErrorMessage}
        message={locationErrorMessage}
        sx={{
          "& .MuiSnackbarContent-root": {
            backgroundColor: "#d32f2f",
            color: "white",
          },
        }}
      />
    </Box>
  );
};
